import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { AppObject, OcctImportMesh, LoaderResult, LoadProgress, UvProjection, MaterialProperties } from '../types'
import { makeReporter } from './progress'
import { fromLogoExtras, isLogoExtras } from '../utils/logoMesh'
import { MODEL_UNIT_MM, readStudioExtras } from '../types'
import { applyUvProjection } from '@/components/ProductProfile/utils/uvGeneration'

export async function loadGLBFile(
  file: File,
  uvProjection: UvProjection = 'none',
  onProgress?: (progress: LoadProgress) => void
): Promise<LoaderResult> {
  const report = makeReporter(onProgress)
  await report('Reading the file', 8)
  const arrayBuffer = await file.arrayBuffer()
  const loader = new GLTFLoader()

  // Set up Draco decoder for compressed meshes
  const dracoLoader = new DRACOLoader()
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
  loader.setDRACOLoader(dracoLoader)

  // Parse the GLB/GLTF file
  await report('Parsing the model', 20)
  const gltf = await new Promise<any>((resolve, reject) => {
    loader.parse(arrayBuffer, '', resolve, reject)
  })

  // Extract all meshes from the scene. We bake world transforms into a working
  // copy of the geometry (for centering/scaling/preview), but ALSO keep the
  // original local geometry + node world matrix so the model can be re-exported
  // faithfully without rotating or renaming the parts.
  const entries: {
    geometry: THREE.BufferGeometry
    color: [number, number, number]
    sourceMaterial: THREE.Material | null
    name: string
    transform: number[]
    sourceAttributes: AppObject['sourceAttributes']
    /** glTF node extras — carries a logo placement when this file came from here. */
    extras: Record<string, unknown>
    /** A logo sheet, whose UVs map its artwork and must survive the load untouched. */
    isLogo: boolean
    /** The source file authored a real PBR surface for this part — see hasAuthoredMaps. */
    authored: boolean
  }[] = []

  gltf.scene.traverse((child: THREE.Object3D) => {
    if ((child as THREE.Mesh).isMesh) {
      const mesh = child as THREE.Mesh
      mesh.updateWorldMatrix(true, false)

      // Snapshot the ORIGINAL local-space geometry and node world transform.
      const srcGeom = mesh.geometry
      const srcPos = srcGeom.getAttribute('position')
      const srcNorm = srcGeom.getAttribute('normal')
      const srcUv = srcGeom.getAttribute('uv')
      const srcTangent = srcGeom.getAttribute('tangent')
      const srcIndex = srcGeom.getIndex()
      const sourceAttributes = {
        position: new Float32Array(srcPos.array as Float32Array),
        normal: srcNorm ? new Float32Array(srcNorm.array as Float32Array) : undefined,
        uv: srcUv ? new Float32Array(srcUv.array as Float32Array) : undefined,
        tangent: srcTangent ? new Float32Array(srcTangent.array as Float32Array) : undefined,
        index: srcIndex ? new Uint32Array(srcIndex.array) : undefined,
      }
      const transform = mesh.matrixWorld.toArray()

      const geometry = mesh.geometry.clone()
      geometry.applyMatrix4(mesh.matrixWorld)

      // Extract color and material reference
      let color: [number, number, number] = [0.8, 0.8, 0.8]
      const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material
      if (mat && (mat as THREE.MeshStandardMaterial).color) {
        const c = (mat as THREE.MeshStandardMaterial).color
        color = [c.r, c.g, c.b]
      }

      entries.push({
        geometry,
        color,
        sourceMaterial: mat || null,
        name: mesh.name || child.name || '',
        transform,
        sourceAttributes,
        extras: (mesh.userData ?? {}) as Record<string, unknown>,
        isLogo: isLogoExtras((mesh.userData as Record<string, unknown> | undefined)?.neatoLogo),
        authored: hasAuthoredMaps(mat),
      })
    }
  })

  if (entries.length === 0) {
    throw new Error('No meshes found in GLB/GLTF file')
  }

  await report(`Building ${entries.length} part${entries.length === 1 ? '' : 's'}`, 55)

  const allGeometries = entries.map((e) => e.geometry)

  // glTF/GLB is defined as Y-up, so the model is already correctly oriented.
  // Unlike STEP/STL (which can be Z-up), we must NOT auto-rotate here — doing so
  // would tip wider-than-tall models onto their side and bake that wrong
  // orientation into the exported file.
  const tempGroup = new THREE.Group()
  allGeometries.forEach((geom) => {
    tempGroup.add(new THREE.Mesh(geom.clone()))
  })
  const overallBox = new THREE.Box3().setFromObject(tempGroup)
  const overallCenter = new THREE.Vector3()
  overallBox.getCenter(overallCenter)

  // Center horizontally and rest the model on the ground (bottom at y=0),
  // preserving original normals from the GLB.
  allGeometries.forEach((geometry) => {
    geometry.translate(-overallCenter.x, -overallBox.min.y, -overallCenter.z)
    // Only compute normals if the GLB didn't provide them
    if (!geometry.getAttribute('normal')) {
      geometry.computeVertexNormals()
    }
  })

  await report('Generating UVs', 78)
  // A file that authored its own PBR maps addresses them through a hand-made UV atlas,
  // usually shared across every part. Projecting over it re-addresses each vertex and the
  // maps land somewhere else entirely — the lettering, the wear and the surface finish all
  // scatter. The projection controls exist to GIVE a wrap to models that have no UVs, so
  // they simply have nothing to do here; the caller also snaps the dropdown to None so the
  // reason is visible rather than silently overridden.
  const authoredMaterials = entries.some((entry) => entry.authored)

  // Apply UV projection if specified (otherwise preserve original UVs).
  //
  // Never to a logo sheet. Its UVs are not a surface parameterisation to be regenerated —
  // they ARE the artwork's mapping, a plain 0..1 grid across the sheet. Reprojecting them
  // cylindrically with the rest of the model smears the artwork across the part, which is
  // what made a re-imported logo come back stretched.
  if (uvProjection !== 'none' && !authoredMaterials) {
    entries.forEach((entry) => {
      if (entry.isLogo) return
      applyUvProjection(entry.geometry, uvProjection)
    })
  }

  // Convert each geometry to AppObject format
  const objects: AppObject[] = []
  let totalVertexCount = 0

  entries.forEach((entry, index) => {
    const geometry = entry.geometry
    const positions = geometry.getAttribute('position').array as Float32Array
    const normals = geometry.getAttribute('normal').array as Float32Array
    const uvAttribute = geometry.getAttribute('uv')
    const tangentAttribute = geometry.getAttribute('tangent')
    const indexAttribute = geometry.getIndex()
    const vertexCount = positions.length / 3
    totalVertexCount += vertexCount

    // Preserve existing index or create sequential
    let indices: Uint32Array
    if (indexAttribute) {
      indices = new Uint32Array(indexAttribute.array)
    } else {
      indices = new Uint32Array(vertexCount)
      for (let i = 0; i < vertexCount; i++) indices[i] = i
    }

    const partId = entry.name || `glb-part-${index}`

    const mesh: OcctImportMesh = {
      id: partId,
      color: entry.color,
      attributes: {
        position: { array: new Float32Array(positions) },
        normal: { array: new Float32Array(normals) },
        ...(uvAttribute && {
          uv: { array: new Float32Array(uvAttribute.array as Float32Array) },
        }),
        ...(tangentAttribute && {
          tangent: { array: new Float32Array(tangentAttribute.array as Float32Array) },
        }),
      },
      index: { array: indices },
    }

    // Extract material properties for this specific object
    const objMaterial = extractMaterialProperties(entry.sourceMaterial, entry.authored)

    objects.push({
      id: partId,
      color: entry.color,
      mesh,
      material: objMaterial,
      sourceTransform: entry.transform,
      sourceAttributes: entry.sourceAttributes,
      // Kept only where the file authored a surface this app cannot describe; everywhere
      // else the synthesised MaterialProperties above is the whole truth.
      sourceMaterial: entry.authored ? (entry.sourceMaterial as THREE.MeshStandardMaterial) : undefined,
    })
  })

  await report('Preparing the preview', 95)
  // Restore logo parts. A file exported from here carries each logo's placement in its
  // node extras; the artwork itself comes back from the material's texture, where it
  // already lives, rather than from a second copy in the JSON.
  entries.forEach((entry, index) => {
    const extras = entry.extras?.neatoLogo
    if (!isLogoExtras(extras)) return
    const mat = entry.sourceMaterial as THREE.MeshStandardMaterial | null
    const image = mat?.map ? textureToDataUrl(mat.map) : null
    if (!image) return
    objects[index].logo = fromLogoExtras(extras, MODEL_UNIT_MM.m, image)
  })

  // Calculate viewing scale
  const finalSize = new THREE.Vector3()
  overallBox.getSize(finalSize)
  const viewingScale = 150 / Math.max(finalSize.x, finalSize.y, finalSize.z)

  // Clean up Draco loader
  dracoLoader.dispose()

  return {
    objects,
    vertexCount: totalVertexCount,
    viewingScale,
    // Model bottom sits at y=0; aim the camera at its vertical midpoint.
    verticalCenter: (finalSize.y * viewingScale) / 2,
    unit: 'm', // glTF defines its coordinates in metres
    // A file exported from here remembers the unit its sizes were shown in.
    displayUnit: readStudioExtras(gltf.scene)?.displayUnit,
    // Undo of the centering applied above — lets parts authored in preview space
    // (a logo sheet) be re-exported in the source file's frame.
    sourceOffset: [overallCenter.x, overallBox.min.y, overallCenter.z],
    authoredMaterials,
  }
}

/**
 * Does this material carry maps the app cannot synthesise or edit — a normal, occlusion,
 * or metal-rough map?
 *
 * This is the line between "a file someone authored elsewhere" and "a file this app made".
 * The export path only ever writes a flattened baseColor `map` (see utils/export.ts), so a
 * material with any of these cannot have come from here, and its look belongs to whoever
 * built it. A lone `map` stays ambiguous on purpose: that IS the round-trip case, where the
 * texture is a baked pattern the user must be able to keep editing.
 */
function hasAuthoredMaps(mat: THREE.Material | null): boolean {
  if (!mat) return false
  const m = mat as THREE.MeshStandardMaterial
  return !!(m.normalMap || m.aoMap || m.metalnessMap || m.roughnessMap)
}

function colorToHex(color: THREE.Color): string {
  return '#' + color.getHexString()
}

function extractMaterialProperties(mat: THREE.Material | null, authored = false): MaterialProperties {
  // Default fallback
  const props: MaterialProperties = {
    preset: 'custom',
    color: '#cccccc',
    metalness: 0,
    roughness: 0.5,
    transmission: 0,
    thickness: 0.5,
    ior: 1.5,
    opacity: 1,
    specularIntensity: 1,
    // No glTF field carries this, so an imported file always starts at the default and
    // the Environment slider takes it from there.
    envMapIntensity: 0.7,
    emissiveColor: '#000000',
    emissiveIntensity: 0,
    side: 'front',
    showUvTestText: false,
    showUvTestPattern: false,
  }

  if (!mat) return props

  // Extract from MeshStandardMaterial / MeshPhysicalMaterial
  const stdMat = mat as THREE.MeshStandardMaterial

  if (stdMat.color) {
    props.color = colorToHex(stdMat.color)
  }
  if (stdMat.metalness !== undefined) {
    props.metalness = stdMat.metalness
  }
  if (stdMat.roughness !== undefined) {
    props.roughness = stdMat.roughness
  }
  if (stdMat.emissive) {
    props.emissiveColor = colorToHex(stdMat.emissive)
  }
  if (stdMat.emissiveIntensity !== undefined) {
    props.emissiveIntensity = stdMat.emissiveIntensity
  }
  if (stdMat.opacity !== undefined) {
    props.opacity = stdMat.opacity
  }

  // Side mapping
  if (stdMat.side === THREE.DoubleSide) {
    props.side = 'double'
  } else if (stdMat.side === THREE.BackSide) {
    props.side = 'back'
  } else {
    props.side = 'front'
  }

  // MeshPhysicalMaterial-specific properties
  const physMat = mat as THREE.MeshPhysicalMaterial
  if (physMat.transmission !== undefined) {
    props.transmission = physMat.transmission
  }
  if (physMat.thickness !== undefined) {
    props.thickness = physMat.thickness
  }
  if (physMat.ior !== undefined) {
    props.ior = physMat.ior
  }
  // KHR_materials_specular, if the file carries it. Without this a matte finish would
  // come back glossy on every re-import, because three defaults the value to 1.
  if (physMat.specularIntensity !== undefined) {
    props.specularIntensity = physMat.specularIntensity
  }

  // Recover a printed pattern from the base-color texture. Exports bake the
  // pattern (flattened over the base color) into `map` with a white color, so
  // reading `map` back into `patternImage` round-trips: re-import shows and lets
  // the user keep editing the pattern instead of a plain white part.
  //
  // A see-through material is NOT a pattern, though. A logo is a cut-out sheet whose
  // transparency is the artwork's shape; compositing it as a pattern floods every clear
  // pixel with the base colour and the logo comes back as a solid card. Those are picked
  // up as logos further down instead.
  //
  // Nor is an AUTHORED base color. On a part whose material also carries a normal or
  // metal-rough map, `map` is an albedo atlas addressed by the model's own UVs — not
  // artwork someone placed. Reading it in as a pattern hands it to compositePatternCanvas,
  // which re-lays it out on a canvas sized to the part's circumference:height and so
  // re-addresses every texel: a 4096² atlas becomes a 2048×141 strip on a pistol slide,
  // and the part renders as an arbitrary dark smear. Those keep their material instead
  // (see AppObject.sourceMaterial) and the pattern controls step aside for them.
  const isCutOut = stdMat.transparent === true || (stdMat.alphaTest ?? 0) > 0
  if (stdMat.map && !isCutOut && !authored) {
    const url = textureToDataUrl(stdMat.map)
    if (url) props.patternImage = url
  }

  return props
}

/** Rasterise a loaded texture to a PNG data URL (textures are decoded once the GLB parse resolves). */
function textureToDataUrl(texture: THREE.Texture): string | null {
  const img = texture.image as (HTMLImageElement | HTMLCanvasElement | ImageBitmap) & {
    width?: number
    height?: number
  }
  if (!img) return null
  const w = img.width
  const h = img.height
  if (!w || !h) return null
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  try {
    ctx.drawImage(img as CanvasImageSource, 0, 0)
    return canvas.toDataURL('image/png')
  } catch {
    // e.g. a tainted/cross-origin image — skip rather than crash the load.
    return null
  }
}
